import {
    Button,
    ButtonVariants,
    Modal,
    ModalProps,
} from '@tcc-online/common-components';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { FormInput, FormField } from '@tcc-online/input-components';
import { useTranslate } from 'react-translate.ts';
import { ObjectSchema, StringSchema } from 'yup';

import styles from './ContactModal.module.scss';
import ContactActions from '../../../actions/ContactActions';
import ProfileSelectors from '../../../selectors/ProfileSelectors';
import AuthSelectors from '../../../selectors/AuthSelectors';
import { TRANSLATIONS_ROOT_KEY } from '../../../constants/translation.constants';
import { AppDispatch } from '../../../reducers/store';

interface ContactModalProps extends ModalProps {
    isLogged: boolean;
}

const EMAIL_FIELD = 'email';
const TEXT_FIELD = 'text';

type Values = {
    [EMAIL_FIELD]: string;
    [TEXT_FIELD]: string;
};

export default function ContactModal(props: ContactModalProps) {
    const { isLogged, onClose, ...rest } = props;
    const dispatch = useDispatch<AppDispatch>();
    const email = ProfileSelectors.useGetEmail();
    const translate = useTranslate(TRANSLATIONS_ROOT_KEY + '.contact');
    const isAnonymous = useSelector(AuthSelectors.isAnonymous);

    const defaultValues: Values = {
        email: (!isAnonymous && email) || '',
        text: '',
    };

    const emailLabel = translate('email');
    const textLabel = translate('message');

    return (
        <Modal onClose={onClose} {...rest}>
            <div className={styles.title}>
                <h5>{translate('title')}</h5>
            </div>
            <div>
                <p>{translate('note')}</p>
            </div>
            <Formik<Values>
                initialValues={defaultValues}
                onSubmit={submit}
                enableReinitialize
                validationSchema={createValidationSchema}
            >
                <Form>
                    {(!isLogged || isAnonymous) && (
                        <FormField>
                            <FormInput name={EMAIL_FIELD} label={emailLabel} />
                        </FormField>
                    )}
                    <FormField>
                        <FormInput
                            name={TEXT_FIELD}
                            label={textLabel}
                            rows={8}
                            multiline
                        />
                    </FormField>
                    <div className={styles.buttons}>
                        <Button type="submit" variant={ButtonVariants.positive}>
                            {translate('send')}
                        </Button>
                    </div>
                </Form>
            </Formik>
        </Modal>
    );

    function submit(values: Values) {
        const { email, text } = values;
        dispatch(ContactActions.sendRequest({ email, message: text }));
        onClose();
    }
}

function createValidationSchema() {
    return new ObjectSchema({
        email: new StringSchema().email().required(),
        text: new StringSchema().required(),
    });
}
